"use client";

import type { LucideIcon } from "lucide-react";
import { AppWindowMac } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { Fragment } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export interface AppItem {
  color?: string;
  disabled?: boolean;
  /**
   * Grouping used to insert a divider between public apps (always visible)
   * and access-gated apps. The divider only renders when both groups have
   * at least one item in the final list.
   */
  group?: "public" | "gated";
  href: string;
  icon?: LucideIcon;
  /**
   * When true, this app represents the user's current location. Rendered with
   * a muted background and is not clickable.
   */
  isActive?: boolean;
  LogoComponent?: ComponentType<SVGProps<SVGSVGElement>>;
  logo?: string;
  name: string;
}

export interface AppSwitcherProps {
  apps: AppItem[];
  className?: string;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

/**
 * Prefetch DNS and establish connection for cross-origin navigation
 * This helps reduce latency when switching between apps on different subdomains
 */
function prefetchAppUrl(url: string) {
  try {
    const urlObj = new URL(url);
    const origin = urlObj.origin;

    // Check if dns-prefetch link already exists
    if (!document.querySelector(`link[rel="dns-prefetch"][href="${origin}"]`)) {
      const dnsPrefetch = document.createElement("link");
      dnsPrefetch.rel = "dns-prefetch";
      dnsPrefetch.href = origin;
      document.head.appendChild(dnsPrefetch);
    }

    // Check if preconnect link already exists
    if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = origin;
      document.head.appendChild(preconnect);
    }
  } catch {
    // Ignore invalid URLs
  }
}

export function AppSwitcher({
  apps,
  className,
  onOpenChange,
  open,
}: AppSwitcherProps) {
  return (
    <DropdownMenu modal={false} onOpenChange={onOpenChange} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Switch app"
          className={className}
          onMouseEnter={() => {
            // Prefetch all app URLs on hover for faster navigation
            for (const app of apps) {
              if (!app.disabled && app.href !== "#") {
                prefetchAppUrl(app.href);
              }
            }
          }}
          size="icon"
          variant="ghost"
        >
          <AppWindowMac className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {apps.map((app, idx) => {
          const previous = apps[idx - 1];
          const showDivider =
            previous && previous.group !== "gated" && app.group === "gated";

          const iconBlock = (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center">
              {app.LogoComponent && (
                <app.LogoComponent className="h-full w-full" />
              )}
              {!app.LogoComponent && app.icon && (
                <app.icon
                  className={
                    app.isActive
                      ? "h-5 w-5 text-foreground"
                      : "h-5 w-5 text-muted-foreground"
                  }
                />
              )}
            </div>
          );

          const item = app.isActive ? (
            <DropdownMenuItem
              aria-current="page"
              className="cursor-default bg-muted/60 focus:bg-muted/60"
              disabled={false}
              onSelect={(e) => e.preventDefault()}
            >
              {iconBlock}
              <span className="font-medium text-sm">{app.name}</span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem asChild disabled={app.disabled}>
              <a
                className="cursor-pointer"
                href={app.disabled ? "#" : app.href}
              >
                {iconBlock}
                <span className="font-medium text-sm">{app.name}</span>
              </a>
            </DropdownMenuItem>
          );

          return (
            <Fragment key={app.name}>
              {showDivider && <DropdownMenuSeparator />}
              {item}
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
